"use client";

import { OptionGroup, type OptionGroupSize } from "@/components/option-group";
import { Field, FieldDescription, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import type { FieldBindingProps, SelectOption } from "./types";

export interface OptionGroupFieldProps extends FieldBindingProps<string> {
  label?: string;
  description?: string;
  options: SelectOption[];
  onOptionHover?: (value: string | undefined) => void;
  disabled?: boolean;
  isRequired?: boolean;
  size?: OptionGroupSize;
  /** Selecting the current option clears the answer. */
  allowDeselect?: boolean;
}

/** Inline single-select field — same contract as `SelectField`, rendered as a
 * responsive button group instead of a dropdown (one click to pick a value). */
export function OptionGroupField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  description,
  options,
  onOptionHover,
  disabled,
  size,
  allowDeselect,
}: OptionGroupFieldProps) {
  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldOptionGroup
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        options={options}
        onOptionHover={onOptionHover}
        disabled={disabled}
        size={size}
        allowDeselect={allowDeselect}
        hasLabel={!!label}
      />
      <FieldError />
    </Field>
  );
}

function FieldOptionGroup({
  value,
  onChange,
  onBlur,
  options,
  onOptionHover,
  disabled,
  size,
  allowDeselect,
  hasLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  options: SelectOption[];
  onOptionHover?: (value: string | undefined) => void;
  disabled?: boolean;
  size?: OptionGroupSize;
  /** Selecting the current option clears the answer. */
  allowDeselect?: boolean;
  hasLabel: boolean;
}) {
  const { id, hasError } = useFieldWrapperContext();
  return (
    <OptionGroup
      id={id}
      value={value}
      allowDeselect={allowDeselect}
      onChange={(v) => {
        onChange(v);
        onBlur();
      }}
      options={options}
      onOptionHover={onOptionHover}
      disabled={disabled}
      size={size}
      aria-invalid={hasError}
      aria-describedby={hasError ? `${id}-error` : undefined}
      className={hasLabel ? "mt-1" : undefined}
    />
  );
}

export interface MultiOptionGroupFieldProps extends FieldBindingProps<string[]> {
  label?: string;
  description?: string;
  options: SelectOption[];
  disabled?: boolean;
  isRequired?: boolean;
  size?: OptionGroupSize;
}

/** Inline multi-select field ("select all that apply") — array-valued sibling
 * of `OptionGroupField`, rendered as a toggle chip group. */
export function MultiOptionGroupField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  description,
  options,
  disabled,
  size,
}: MultiOptionGroupFieldProps) {
  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldMultiOptionGroup
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        options={options}
        disabled={disabled}
        size={size}
        hasLabel={!!label}
      />
      <FieldError />
    </Field>
  );
}

function FieldMultiOptionGroup({
  value,
  onChange,
  onBlur,
  options,
  disabled,
  size,
  hasLabel,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  onBlur: () => void;
  options: SelectOption[];
  disabled?: boolean;
  size?: OptionGroupSize;
  hasLabel: boolean;
}) {
  const { id, hasError } = useFieldWrapperContext();
  return (
    <OptionGroup
      multiple
      id={id}
      value={value ?? []}
      onChange={(v) => {
        onChange(v);
        onBlur();
      }}
      options={options}
      disabled={disabled}
      size={size}
      aria-invalid={hasError}
      aria-describedby={hasError ? `${id}-error` : undefined}
      className={hasLabel ? "mt-1" : undefined}
    />
  );
}
